// Mock data for ARCHE Engineering Solutions

export const heroData = {
  headline: "Engineering Excellence",
  headlineAccent: "from Concept to Production",
  description: "ARCHE Engineering Solutions is a trusted engineering and manufacturing partner delivering end-to-end product development services—from concept design, reverse engineering, and 3D scanning to rapid prototyping, precision manufacturing, tooling, quality inspection, and scalable production solutions.",
  descriptionExtended: "We help startups, OEMs, and industrial businesses transform ideas into high-quality products through innovative engineering, advanced manufacturing technologies, and reliable production support.",
  cta: [
    { text: "Request a Quote", primary: true },
    { text: "Explore Our Services", primary: false }
  ]
};

export const aboutData = {
  title: "Who We Are",
  description: "We are an engineering and manufacturing company helping startups, SMEs and enterprises convert innovative ideas into production-ready products. We provide complete product development solutions under one roof.",
  stats: [
    { value: "100+", label: "Projects Delivered" },
    { value: "50+", label: "Clients Worldwide" }
  ]
};

export const servicesData = [
  {
    id: 1,
    title: "Product Design & Development",
    description: "Complete industrial and mechanical product design services",
    icon: "Pen",
    features: ["Industrial Design", "Mechanical Design", "3D CAD Modeling", "Design Validation", "DFM Analysis"],
    details: {
      overview: "ARCHE offers end-to-end product design and development services, transforming concepts into market-ready products. Our multidisciplinary team combines industrial aesthetics, mechanical engineering rigor, and manufacturing intelligence to build products that perform, sell, and scale. From initial ideation sketches to production-ready 3D CAD files, we align every design decision with your brand, budget, and business objectives.",
      keyFeatures: [
        "Concept sketching and ideation",
        "3D CAD modeling using SolidWorks, Creo, and Fusion 360",
        "Design for Manufacturing (DFM) and Design for Assembly (DFA)",
        "Photorealistic product renders and animations",
        "Complete engineering drawings and BOM"
      ],
      applications: ["Consumer electronics", "Medical devices", "Industrial equipment", "Automotive components", "Home appliances"],
      benefits: ["Faster time-to-market", "Optimized manufacturing cost", "Improved product reliability", "IP-secured design process"],
      industries: ["Consumer Electronics", "Medical", "Automotive", "Industrial Automation"]
    }
  },
  {
    id: 2,
    title: "Reverse Engineering & 3D Scanning",
    description: "Convert physical parts into precise 3D digital models",
    icon: "Scan",
    features: ["3D Scanning", "CAD Reconstruction", "Geometry Capture", "Legacy Part Recovery", "Dimensional Analysis"],
    details: {
      overview: "Our reverse engineering and 3D scanning services turn physical objects into precise, editable digital models. Using industrial-grade structured light and blue-light scanners, we capture complex geometries with micron-level accuracy. Whether you need to recreate obsolete parts, digitize legacy tooling, or benchmark competitor products, ARCHE delivers manufacturing-ready CAD from real-world objects—fast and accurately.",
      keyFeatures: [
        "High-resolution 3D scanning up to 0.02mm accuracy",
        "Parametric CAD reconstruction from scan data",
        "STL, STEP, IGES output formats",
        "Dimensional inspection and deviation reports",
        "Surface, freeform, and hybrid modeling"
      ],
      applications: ["Legacy part replication", "Obsolete component recovery", "Custom fitting and retrofitting", "Heritage restoration", "Quality benchmarking"],
      benefits: ["Recover unavailable spare parts", "Modernize legacy designs", "Reduce documentation gaps", "Enable design improvements"],
      industries: ["Aerospace", "Automotive", "Heritage & Museums", "Industrial Machinery"]
    }
  },
  {
    id: 3,
    title: "Rapid Prototyping",
    description: "Functional and appearance prototypes in days, not weeks",
    icon: "Zap",
    features: ["Functional Prototypes", "Concept Models", "Appearance Models", "Engineering Validation", "Iterative Development"],
    details: {
      overview: "Turn ideas into tangible prototypes in as little as 24–72 hours. ARCHE's rapid prototyping services help you validate form, fit, and function before investing in expensive tooling. We use a mix of 3D printing, CNC machining, and vacuum casting to deliver prototypes that closely match production intent. Iterate faster, test more, and de-risk product launches with confidence.",
      keyFeatures: [
        "Same-week turnaround on most prototypes",
        "Multi-material and multi-color printing",
        "Post-processing: painting, plating, texturing",
        "Functional testing-grade prototypes",
        "Design iteration support with feedback loops"
      ],
      applications: ["Product validation", "Investor demos", "User testing", "Trade show samples", "Design reviews"],
      benefits: ["Validate designs early", "Reduce development risk", "Speed up decision making", "Cost-effective iterations"],
      industries: ["Startups", "Consumer Products", "Medical Devices", "Electronics", "Robotics"]
    }
  },
  {
    id: 4,
    title: "3D Printing & Additive Manufacturing",
    description: "Industrial-grade FDM, SLA, SLS, MJF and DMLS printing",
    icon: "Printer",
    features: ["FDM Printing", "SLA Printing", "SLS Printing", "MJF & DLP", "DMLS Metal Printing"],
    details: {
      overview: "ARCHE operates a full spectrum of industrial 3D printing technologies to support prototype, low-volume, and end-use production needs. From strong FDM parts in engineering thermoplastics to fine-detail SLA resins and durable SLS nylon, our fleet handles every geometry and material requirement. Metal DMLS capabilities enable direct printing of aluminum, stainless steel, and titanium components for demanding applications.",
      keyFeatures: [
        "7+ printing technologies under one roof",
        "50+ certified engineering materials",
        "Build volumes up to 1000×1000×1000mm",
        "Aerospace-grade metal printing",
        "In-house post-processing and finishing"
      ],
      applications: ["Functional end-use parts", "Jigs and fixtures", "Complex geometries", "Lightweight structures", "Bridge production"],
      benefits: ["No tooling investment", "Complex shapes possible", "Material flexibility", "Quick production ramp-up"],
      industries: ["Aerospace", "Automotive", "Medical", "Defense", "Consumer Products"]
    }
  },
  {
    id: 5,
    title: "CNC Machining & Precision Manufacturing",
    description: "3, 4, and 5-axis CNC machining with tight tolerances",
    icon: "Cog",
    features: ["3-Axis Milling", "5-Axis Machining", "CNC Turning", "Laser Cutting", "Sheet Metal Fabrication"],
    details: {
      overview: "Our CNC machining facility delivers precision-manufactured components in metals and engineering plastics with tolerances as tight as ±0.01mm. Equipped with modern 3-axis, 4-axis, and 5-axis CNC machines, we handle everything from single prototypes to production batches. Complementary services like laser cutting, sheet metal fabrication, and surface finishing make us a true one-stop machining partner.",
      keyFeatures: [
        "3, 4, and 5-axis CNC milling & turning",
        "Tolerances up to ±0.01mm",
        "Materials: Aluminum, Steel, Titanium, Brass, Copper, Plastics",
        "In-house CMM and inspection",
        "Surface treatments: anodizing, plating, powder coating"
      ],
      applications: ["Precision components", "Mold and die parts", "Aerospace parts", "Medical instruments", "Industrial machinery"],
      benefits: ["Superior surface finish", "Tight tolerances", "Wide material choice", "Consistent quality"],
      industries: ["Aerospace", "Automotive", "Medical", "Defense", "Industrial Automation"]
    }
  },
  {
    id: 6,
    title: "Injection Molding & Tooling",
    description: "Tool design, mold making, and plastic injection molding",
    icon: "Factory",
    features: ["Mold Design", "Tool Development", "Plastic Injection", "Insert Molding", "Overmolding"],
    details: {
      overview: "From single-cavity prototype tools to multi-cavity production molds, ARCHE handles the full injection molding lifecycle. Our in-house tool room designs and manufactures precision molds, while our press-shop runs a full range of tonnage machines. We support low-volume bridge production and high-volume mass manufacturing with full quality documentation and PPAP support.",
      keyFeatures: [
        "Mold design and manufacturing",
        "Single and multi-cavity molds",
        "Injection molding presses from 50T to 500T",
        "Insert molding and overmolding",
        "PPAP, FAI, and process validation"
      ],
      applications: ["Consumer product housings", "Automotive components", "Medical enclosures", "Electronics parts", "Packaging"],
      benefits: ["Low unit cost at volume", "Consistent quality", "Complex geometries", "Wide material range"],
      industries: ["Consumer Electronics", "Automotive", "Medical", "FMCG", "Industrial"]
    }
  },
  {
    id: 7,
    title: "Quality Inspection & Metrology",
    description: "CMM, 3D scanning, and full dimensional inspection",
    icon: "Award",
    features: ["CMM Inspection", "3D Scan Inspection", "First Article Reports", "PPAP Documentation", "GD&T Analysis"],
    details: {
      overview: "Quality is non-negotiable at ARCHE. Our climate-controlled metrology lab is equipped with coordinate measuring machines (CMMs), optical scanners, and precision gauges to verify every part against your specifications. From First Article Inspections (FAI) to full PPAP submissions, our quality team delivers documentation that meets IATF 16949, ISO 13485, and AS9100 expectations.",
      keyFeatures: [
        "Zeiss and Hexagon CMM systems",
        "Blue-light 3D scan inspection",
        "GD&T interpretation and reporting",
        "First Article Inspection (FAI) reports",
        "Full PPAP documentation packages"
      ],
      applications: ["Production part validation", "Supplier quality auditing", "Reverse engineering QC", "Failure analysis", "Compliance reporting"],
      benefits: ["Traceable quality data", "Reduce rejection rates", "Meet regulatory standards", "Data-driven improvements"],
      industries: ["Aerospace", "Automotive", "Medical", "Defense"]
    }
  },
  {
    id: 8,
    title: "Contract Manufacturing & Production",
    description: "End-to-end production, assembly, and supply chain support",
    icon: "Package",
    features: ["Low Volume Manufacturing", "Mass Production", "Assembly Services", "Supply Chain", "Packaging"],
    details: {
      overview: "ARCHE serves as an extended manufacturing arm for startups, OEMs, and enterprises. Whether you need a bridge production run of 100 units or scaled manufacturing of 100,000+, our contract manufacturing services deliver on quality, cost, and timeline. We manage the complete supply chain—from raw material sourcing to sub-assembly, final assembly, testing, packaging, and global logistics.",
      keyFeatures: [
        "Flexible production volumes",
        "Sub-assembly and final assembly",
        "Multi-vendor supply chain management",
        "In-line testing and quality gates",
        "Custom packaging and global shipping"
      ],
      applications: ["Product launches", "Bridge manufacturing", "Sustained production", "Regional expansion", "OEM programs"],
      benefits: ["Lower operational overhead", "Faster market entry", "Scalable capacity", "Single-point accountability"],
      industries: ["Startups", "OEMs", "Consumer Products", "Industrial Equipment"]
    }
  }
];

export const printingTechData = [
  {
    id: 1,
    name: "FDM",
    fullName: "Fused Deposition Modeling",
    overview: "Most common and affordable 3D printing technology",
    advantages: ["Cost-effective", "Wide material range", "Quick turnaround"],
    materials: ["PLA", "ABS", "PETG", "Nylon", "TPU"],
    accuracy: "±0.5mm",
    surfaceFinish: "Medium",
    leadTime: "1-3 days"
  },
  {
    id: 2,
    name: "SLA",
    fullName: "Stereolithography",
    overview: "High-resolution resin-based printing",
    advantages: ["Excellent surface finish", "High accuracy", "Fine details"],
    materials: ["Standard Resin", "Engineering Resin", "Castable Resin"],
    accuracy: "±0.1mm",
    surfaceFinish: "Excellent",
    leadTime: "2-4 days"
  },
  {
    id: 3,
    name: "SLS",
    fullName: "Selective Laser Sintering",
    overview: "Powder-based printing for functional parts",
    advantages: ["No support structures", "Strong parts", "Complex geometries"],
    materials: ["Nylon PA12", "Nylon PA11", "TPU"],
    accuracy: "±0.3mm",
    surfaceFinish: "Good",
    leadTime: "3-5 days"
  },
  {
    id: 4,
    name: "DMLS",
    fullName: "Direct Metal Laser Sintering",
    overview: "Metal 3D printing for industrial applications",
    advantages: ["Metal parts", "High strength", "Production-grade"],
    materials: ["Aluminium", "Steel", "Titanium"],
    accuracy: "±0.1mm",
    surfaceFinish: "Good",
    leadTime: "5-7 days"
  }
];

export const industriesData = [
  { name: "Automotive", icon: "Car" },
  { name: "Medical", icon: "Heart" },
  { name: "Healthcare", icon: "Activity" },
  { name: "Consumer Electronics", icon: "Smartphone" },
  { name: "Industrial Automation", icon: "Settings" },
  { name: "Robotics", icon: "Bot" },
  { name: "Aerospace", icon: "Rocket" },
  { name: "Defense", icon: "Shield" },
  { name: "Packaging", icon: "Package" },
  { name: "FMCG", icon: "ShoppingCart" },
  { name: "Education", icon: "GraduationCap" },
  { name: "IoT", icon: "Wifi" }
];

export const processSteps = [
  { step: 1, title: "Idea Discussion", description: "Understanding your vision" },
  { step: 2, title: "Requirement Analysis", description: "Detailed specifications" },
  { step: 3, title: "Industrial Design", description: "Conceptual design" },
  { step: 4, title: "CAD Engineering", description: "3D modeling" },
  { step: 5, title: "Simulation", description: "Virtual testing" },
  { step: 6, title: "Prototype Development", description: "Physical prototype" },
  { step: 7, title: "Testing", description: "Quality validation" },
  { step: 8, title: "Design Optimization", description: "Refinement" },
  { step: 9, title: "Tooling", description: "Production tooling" },
  { step: 10, title: "Manufacturing", description: "Mass production" },
  { step: 11, title: "Quality Inspection", description: "Final checks" },
  { step: 12, title: "Delivery", description: "Global shipping" }
];

export const faqData = [
  {
    id: 1,
    question: "What is the typical turnaround time for prototypes?",
    answer: "Turnaround time depends on the technology and complexity. FDM prints can be ready in 24-48 hours, while more complex SLA or SLS parts typically take 3-5 days. Metal DMLS parts require 5-7 days."
  },
  {
    id: 2,
    question: "What file formats do you accept?",
    answer: "We accept all common CAD formats including STEP, IGES, STL, OBJ, Parasolid, and native files from SolidWorks, CATIA, Creo, Inventor, and Fusion 360."
  },
  {
    id: 3,
    question: "Do you provide design services?",
    answer: "Yes, we offer complete industrial design and engineering services. Our team can help you from concept to production, including 3D CAD modeling, DFM analysis, and design optimization."
  },
  {
    id: 4,
    question: "What industries do you serve?",
    answer: "We serve a wide range of industries including Automotive, Medical, Aerospace, Consumer Electronics, Robotics, IoT, and Industrial Automation."
  },
  {
    id: 5,
    question: "Do you handle low volume manufacturing?",
    answer: "Absolutely! We specialize in low to medium volume production runs (10 to 10,000+ units) using various technologies including injection molding, CNC machining, and additive manufacturing."
  },
  {
    id: 6,
    question: "What is your quality assurance process?",
    answer: "We follow ISO 9001:2015 certified processes with comprehensive quality checks at every stage. Our facility includes CMM inspection, 3D scanning, and detailed quality documentation."
  }
];

export const contactInfo = {
  email: "sales@archeengineeringsolutions.com",
  phone: "+91 70410 60625",
  phoneRaw: "7041060625",
  addressLines: [
    "D/56 Nathdwar Township,",
    "Near Rajnagar, Somatalav,",
    "Vadodara – 390025,",
    "Gujarat, India"
  ],
  addressShort: "D/56 Nathdwar Township, Near Rajnagar, Somatalav, Vadodara – 390025, Gujarat, India",
  hours: "Mon - Sat: 9:00 AM - 6:00 PM"
};
